import type {InputType} from 'airship/patterns/types'

import type {Props as LabelProps} from './Label'

export interface ValidationHandler<T> {
  (data?: T): void
}

export interface AsyncValidationHandler<T> {
  (data?: T): Promise<void>
}

export type InputProps<T> = InputType<T> & LabelProps

export enum ErrorType {
  Invalid,
  InvalidFormat,
  Required,
  MinMaxValue,
  MaxLength,
  MinLength,
  MaxMagnitude,
  MinMagnitude,
  HandlebarsPayloadInvalid,
  HandlebarsSyntaxInvalid,
  HandlebarsRenderingTimeout,
  HandlebarsUnsupported,
  NoSpecialChar,
  AlreadyExists,
  NoSpaceCharBeginOrEnd,
  NoUppercaseLettersAllowed,
  NoSpacesAllowed,
  InvalidKeypath,
  MissingUnsubscribeLink,
  UnsubscribePageNotSupported,
  CustomUnsubscribePageMissingHref,
  DuplicateExists,
  MissingValue,
  MissingTagValue,
  MissingNumberValue,
  InvalidURL,
  DomainDoesNotMatchUrl,
  XdfEmptyPlaceholder,
  XdfTooManyPlaceholders,
  XdfDoesNotExist,
  MustStartWithLetter,
  InternalUnderscoresOnly,
  MustEndWithLetterOrNumber,
  AirshipReservedNamespace,
  FeedWithThisIDAlreadyExists,
  RequiresPlainOrHtmlField,
  MissingSegmentRows,
  InvalidCidr,
  ContainsExternalPartialReference,
  EqualInvalidFormat,
  OtherOperatorsInvalidFormat,
  SingleValueRequired,
  PreferenceCenterRequired,
  PreferenceCenterSelectionRequired,
  ChannelTypeRequired,
  EqualsStandsAlone,
  EqualsCantBePaired,
  DuplicatedRangeValues,
  ImpossibleRangeValues,
  OverlappingRangeValues,
  MustHaveAtLeastTwoValidItems,
  Unique,
  TagGroupReservedGroupName,
  GetMessage,
  TextRequired,
  MediaRequired,
  MediaAndTextRequired,
  ActionAndTextRequired,
  NotValidMediaType,
  InvalidEmail,
  CustomEventRequired,
  ConditionsRequired,
  ScreenRequired,
  CancellationEventRequired,
  TargetedAudienceConditionRequired,
  FeatureFlagAudienceConditionRequired,
}

export enum WarningType {
  HasCustomUnsubscribePage,
  TooLongForSomeDevices,
  ContainsFigmaMarkup,
}

// taken from kaneda.proto; any numbers above 1000 are non-service failures
export enum HandlebarsErrorCode {
  Validation = 20,
  Rendering = 30,
  Timeout = 40,
}

export const handlebarsErrorCodeToErrorType: Map<
  HandlebarsErrorCode,
  ErrorType
> = new Map([
  [HandlebarsErrorCode.Validation, ErrorType.HandlebarsPayloadInvalid],
  [HandlebarsErrorCode.Rendering, ErrorType.HandlebarsSyntaxInvalid],
  [HandlebarsErrorCode.Timeout, ErrorType.HandlebarsRenderingTimeout],
])

// suggestion from docs:
// https://github.com/Microsoft/TypeScript/wiki/FAQ#why-doesnt-extending-built-ins-like-error-array-and-map-work
export class ValidationError extends Error {
  public type: ErrorType

  public constructor(msg: string, type: ErrorType = ErrorType.Invalid) {
    super(msg)
    Object.setPrototypeOf(this, ValidationError.prototype)
    this.name = 'ValidationError'
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (Error.captureStackTrace) {
      // only available on V8
      Error.captureStackTrace(this, ValidationError)
    }
    this.message = msg
    this.type = type
  }
}

export class HandlebarsSyntaxValidationError extends ValidationError {
  detail: string | undefined

  public constructor(
    msg: string,
    type: ErrorType = ErrorType.Invalid,
    detail: string | undefined
  ) {
    super(msg, type)
    Object.setPrototypeOf(this, HandlebarsSyntaxValidationError.prototype)
    this.name = 'HandlebarsSyntaxValidationError'
    this.detail = detail
  }
}

export class ValidationWarning extends Error {
  public type: WarningType

  public constructor(msg: string, type: WarningType) {
    super(msg)
    Object.setPrototypeOf(this, ValidationWarning.prototype)
    this.name = 'ValidationWarning'
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (Error.captureStackTrace) {
      // only available on V8
      Error.captureStackTrace(this, ValidationWarning)
    }
    this.message = msg
    this.type = type
  }
}

export class UnsupportedLegacyAudience extends Error {
  public constructor(msg: string) {
    super(msg)
    Object.setPrototypeOf(this, UnsupportedLegacyAudience.prototype)
    this.name = 'UnsupportedLegacyAudience'
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (Error.captureStackTrace) {
      // only available on V8
      Error.captureStackTrace(this, UnsupportedLegacyAudience)
    }
    this.message = msg
  }
}

export interface ErrorState {
  valid: boolean
  runtimeError?: Error
  invalidFields: string[]
  errors: {[key: string]: ValidationError[]}
  warnings: {[key: string]: ValidationWarning[]}
}
